/**
 * Facebook Pixel Integration
 * 
 * Provides initialization and event tracking for Facebook/Meta advertising.
 * Events tracked: PageView, Lead, Subscribe, ViewContent, Search
 * 
 * NOTE: All tracking is disabled on native iOS/Android to avoid
 * App Tracking Transparency (ATT) requirements and App Store rejection.
 */

import { isNativePlatform } from '@/utils/nativePlugins';

// Pixel ID from Facebook Events Manager
const FB_PIXEL_ID = '2040177730244538';

// Extend Window interface for Facebook Pixel
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}

/**
 * Initialize Facebook Pixel
 * Loads the Pixel script and fires initial PageView
 */
export const initFacebookPixel = (): void => {
  // Skip on native platforms to avoid ATT requirements
  if (isNativePlatform()) return;
  
  // Skip in development if needed (optional - remove if you want to test in dev)
  // if (import.meta.env.DEV) return;

  // Prevent double initialization
  if (window.fbq) return;

  try {
    // Initialize fbq function before script loads
    const fbq = function(...args: any[]) {
      // @ts-ignore
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
    };
    
    // @ts-ignore
    if (!window._fbq) window._fbq = fbq;
    // @ts-ignore
    fbq.push = fbq;
    // @ts-ignore
    fbq.loaded = true;
    // @ts-ignore
    fbq.version = '2.0';
    // @ts-ignore
    fbq.queue = [];
    
    window.fbq = fbq;

    // Load the Pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    script.onerror = () => {
      console.warn('Facebook Pixel script failed to load');
    };
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    // Initialize with Pixel ID
    window.fbq('init', FB_PIXEL_ID);
    
    // Track initial page view
    window.fbq('track', 'PageView');
  } catch (error) {
    console.warn('Facebook Pixel initialization failed:', error);
  }
};

/**
 * Track a page view
 * Call this on every route change in SPA
 */
export const trackPageView = (): void => {
  if (isNativePlatform() || !window.fbq) return;
  
  try {
    window.fbq('track', 'PageView');
  } catch (error) {
    console.warn('Facebook Pixel PageView tracking failed:', error);
  }
};

/**
 * Track a standard Facebook event
 * @param eventName - Standard event name (Lead, Subscribe, ViewContent, Search, etc.)
 * @param parameters - Optional event parameters
 */
export const trackEvent = (
  eventName: string, 
  parameters?: Record<string, any>
): void => {
  if (isNativePlatform() || !window.fbq) return;
  
  try {
    if (parameters) {
      window.fbq('track', eventName, parameters);
    } else {
      window.fbq('track', eventName);
    }
  } catch (error) {
    console.warn(`Facebook Pixel ${eventName} tracking failed:`, error);
  }
};

/**
 * Track Lead event - when user completes registration
 * @param userType - 'buyer' or 'seller'
 * @param contentName - Optional name for the lead source
 */
export const trackLead = (userType: 'buyer' | 'seller', contentName?: string): void => {
  trackEvent('Lead', {
    content_name: contentName || `${userType.charAt(0).toUpperCase() + userType.slice(1)} Registration`,
    content_category: userType,
  });
};

/**
 * Track Subscribe event - when user starts a paid subscription
 * @param value - Subscription amount (e.g., 9.99 for monthly)
 * @param currency - Currency code (default: USD)
 * @param planType - 'monthly' or 'annual'
 * @param userType - 'buyer' or 'seller'
 */
export const trackSubscribe = (
  value: number,
  planType: 'monthly' | 'annual',
  userType: 'buyer' | 'seller',
  currency: string = 'USD'
): void => {
  // Calculate predicted LTV (12 months for monthly, 1 year for annual)
  const predictedLTV = planType === 'monthly' ? value * 12 : value;
  
  trackEvent('Subscribe', {
    value,
    currency,
    predicted_ltv: predictedLTV,
    content_name: `${planType.charAt(0).toUpperCase() + planType.slice(1)} ${userType.charAt(0).toUpperCase() + userType.slice(1)} Plan`,
    content_category: userType,
  });
};

/**
 * Track ViewContent event - when user views a property
 * @param propertyId - Unique property identifier
 * @param price - Property price (optional)
 */
export const trackViewContent = (propertyId: string, price?: number): void => {
  const params: Record<string, any> = {
    content_type: 'property',
    content_ids: [propertyId],
  };
  
  if (price) {
    params.value = price;
    params.currency = 'USD';
  }
  
  trackEvent('ViewContent', params);
};

/**
 * Track Search event - when user performs property search
 * @param searchLocation - Location string user searched for
 * @param filters - Optional additional search filters
 */
export const trackSearch = (
  searchLocation: string,
  filters?: Record<string, any>
): void => {
  trackEvent('Search', {
    search_string: searchLocation,
    content_category: 'property_search',
    ...filters,
  });
};
